/**
 * CertSubmissionViewModal - Reusable modal for viewing certification submission details
 * Used by: UserHome dashboard, Certification page
 *
 * Guardrails (added 2026-05-12 after the Philip Palmgren incident):
 * 1. Dwell timer — Verify / Authorise / Return stay disabled for the first
 *    30 seconds the modal is open so the verifier cannot rubber-stamp.
 * 2. Non-compliance banner — when the operator flagged step 8 as
 *    non-compliant, render the operator's reason at the top in a red
 *    strip so the verifier cannot miss it.
 * 3. Non-compliant authorise — replaces the plain Authorise button with
 *    "Return to Operator" and "Authorise as Non-Compliant" (the latter
 *    requires explicit confirmation with the reason shown back).
 */

import { useEffect, useState } from 'react';

const VERIFY_DWELL_SECONDS = 30;

interface StatusEntry {
  label: string;
  color: string;
  bg: string;
}

// Status configuration for certification submissions
const STATUS_CONFIG: Record<string, StatusEntry> = {
  draft: { label: 'Draft', color: 'var(--color-text-light)', bg: 'var(--color-bg)' },
  pending_verification: { label: 'Pending Verification', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  pending_authorization: { label: 'Pending Authorisation', color: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  certified: { label: 'Certified', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  returned: { label: 'Returned', color: 'var(--color-error)', bg: 'var(--color-error-bg)' }
};

interface EquipmentItem {
  equipment_type?: string;
  model?: string;
  serial_number?: string;
}

interface Step8Payload {
  protocol_compliance?: boolean;
  non_compliance_reason?: string | null;
  signature_name?: string;
  signed_at?: string;
}

interface CertChecklistData {
  step8?: Step8Payload;
  [key: string]: unknown;
}

interface CertSubmission {
  id?: string;
  submission_status: string;
  customer_name?: string;
  service_location?: string;
  invoice_number?: string;
  service_date?: string;
  operator_name?: string;
  equipment_used?: EquipmentItem[];
  primary_asset_description?: string;
  additional_items_description?: string;
  created_at?: string;
  submitted_at?: string | null;
  verified_at?: string;
  verifier_name?: string;
  authorized_at?: string;
  authorizer_name?: string;
  checklist_data?: CertChecklistData;
}

interface CertSubmissionViewModalProps {
  submission: CertSubmission | null;
  onClose: () => void;
  onVerify?: (submission: CertSubmission) => void;
  onAuthorize?: (submission: CertSubmission) => void;
  onReturn?: (submission: CertSubmission) => void;
  canVerify?: boolean;
  canAuthorize?: boolean;
  formatDate: (dateStr: string | null | undefined) => string;
}

function CertSubmissionViewModal({
  submission,
  onClose,
  onVerify,
  onAuthorize,
  onReturn,
  canVerify = false,
  canAuthorize = false,
  formatDate
}: CertSubmissionViewModalProps) {
  // Dwell timer — disables action buttons for the first VERIFY_DWELL_SECONDS
  // seconds the modal is open so the verifier cannot rubber-stamp.
  const [dwellRemaining, setDwellRemaining] = useState(VERIFY_DWELL_SECONDS);

  useEffect(() => {
    if (!submission) return;
    setDwellRemaining(VERIFY_DWELL_SECONDS);
    const interval = setInterval(() => {
      setDwellRemaining(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when a different submission opens
  }, [submission?.id]);

  if (!submission) return null;

  const status = STATUS_CONFIG[submission.submission_status] ?? STATUS_CONFIG.draft!;
  const dwellActive = dwellRemaining > 0;

  // Non-compliance: operator explicitly flagged step 8 as non-compliant.
  // Treat anything that isn't an explicit true as compliant (back-compat
  // with old submissions that didn't have this field).
  const step8 = submission.checklist_data?.step8;
  const operatorMarkedNonCompliant = step8?.protocol_compliance === false;
  const nonComplianceReason = step8?.non_compliance_reason || '';

  const showVerifyAction = submission.submission_status === 'pending_verification' && canVerify;
  const showAuthorizeAction = submission.submission_status === 'pending_authorization' && canAuthorize;
  const showReturnAction = (showVerifyAction || showAuthorizeAction) && !!onReturn;

  function handleAuthoriseClick() {
    if (!onAuthorize || !submission) return;
    if (operatorMarkedNonCompliant) {
      const reasonPreview = nonComplianceReason
        ? `\n\nOperator's stated reason:\n${nonComplianceReason}`
        : '';
      const ok = window.confirm(
        `This certificate was flagged as NON-COMPLIANT by the operator.${reasonPreview}\n\nAuthorising will issue a certificate that publicly shows the non-compliance flag. Are you sure?`
      );
      if (!ok) return;
    }
    onAuthorize(submission);
  }

  // Parse customer name and address from service_location
  let customerName = submission.customer_name || '—';
  let serviceAddress = '—';
  if (submission.service_location) {
    const parts = submission.service_location.split(' - ');
    if (!submission.customer_name) {
      customerName = parts[0] || '—';
    }
    serviceAddress = parts.slice(1).join(' - ') || '—';
  }

  // Group equipment by type
  const groupedEquipment: Record<string, EquipmentItem[]> = {};
  if (submission.equipment_used && Array.isArray(submission.equipment_used)) {
    submission.equipment_used.forEach(eq => {
      const type = eq.equipment_type || 'Other';
      if (!groupedEquipment[type]) groupedEquipment[type] = [];
      groupedEquipment[type].push(eq);
    });
  }

  // Parse items
  const parseItems = (description: string | null | undefined): string[] => {
    if (!description) return [];
    return description.split(';').map(i => i.trim()).filter(Boolean);
  };

  const primaryItems = parseItems(submission.primary_asset_description);
  const additionalItems = parseItems(submission.additional_items_description);

  const dwellTitle = dwellActive
    ? `Please review the submission first — action available in ${dwellRemaining}s`
    : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={e => e.stopPropagation()}
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}
      >
        {/* Header */}
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div className="flex items-center gap-md">
            <h2 className="m-0">Certification Submission</h2>
            <span
              className="status-badge"
              style={{ background: status.bg, color: status.color }}
            >
              {status.label}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          {/* Non-compliance banner — sits at the very top when the
              operator flagged step 8 as non-compliant. */}
          {operatorMarkedNonCompliant && (
            <div
              style={{
                background: 'var(--color-error-bg)',
                border: '1px solid #dc2626',
                borderRadius: 'var(--radius)',
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--color-error-text)', marginBottom: '0.25rem' }}>
                ⚠ Operator flagged this submission as NON-COMPLIANT
              </div>
              {nonComplianceReason ? (
                <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-error-text)', whiteSpace: 'pre-wrap' }}>
                  <strong>Reason:</strong> {nonComplianceReason}
                </div>
              ) : (
                <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-error-text)', fontStyle: 'italic' }}>
                  No reason provided. Return to operator for explanation before authorising.
                </div>
              )}
            </div>
          )}

          {/* Service Information */}
          <div className="view-section">
            <div className="section-title">Service Information</div>
            <div className="view-row">
              <span className="view-label">Reference</span>
              <span className="code-chip">{submission.invoice_number || '—'}</span>
            </div>
            <div className="view-row">
              <span className="view-label">Service Date</span>
              <span>{formatDate(submission.service_date)}</span>
            </div>
            <div className="view-row">
              <span className="view-label">Customer</span>
              <span>{customerName}</span>
            </div>
            {serviceAddress !== '—' && (
              <div className="view-row">
                <span className="view-label">Location</span>
                <span style={{ textAlign: 'right', maxWidth: '60%' }}>{serviceAddress}</span>
              </div>
            )}
          </div>

          {/* Operator Information */}
          <div className="view-section">
            <div className="section-title">Performed By</div>
            <div className="flex items-center gap-sm">
              <span className="fw-500">{submission.operator_name || '—'}</span>
              <span
                className="status-badge"
                style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', fontSize: 'var(--text-sm)' }}
              >
                ACCREDITED
              </span>
            </div>
          </div>

          {/* Equipment Used */}
          {Object.keys(groupedEquipment).length > 0 && (
            <div className="view-section">
              <div className="section-title">Equipment Used</div>
              {Object.entries(groupedEquipment).map(([type, items]) => (
                <div className="mb-md" key={type}>
                  <div className="fw-500 text-primary mb-xs">
                    {type}
                    <span
                      className="status-badge"
                      style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', fontSize: 'var(--text-xs)', marginLeft: '0.5rem' }}
                    >
                      CERTIFIED
                    </span>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--color-text-light)' }}>
                    {items.map((eq, idx) => (
                      <li className="text-sm" key={idx}>
                        {eq.model || 'Unknown Model'} ({eq.serial_number || '—'})
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Items Serviced */}
          {(primaryItems.length > 0 || additionalItems.length > 0) && (
            <div className="view-section">
              <div className="section-title">Items Serviced</div>

              {primaryItems.length > 0 && (
                <div className="mb-md">
                  <div className="fw-500 text-primary mb-xs">
                    Primary Assets (Beds)
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--color-text-light)' }}>
                    {primaryItems.map((item, idx) => (
                      <li className="text-sm" key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {additionalItems.length > 0 && (
                <div>
                  <div className="fw-500 text-primary mb-xs">
                    Additional Items
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--color-text-light)' }}>
                    {additionalItems.map((item, idx) => (
                      <li className="text-sm" key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Workflow History */}
          <div className="view-section" style={{ borderBottom: 'none' }}>
            <div className="section-title">Workflow</div>
            <div className="flex flex-col gap-sm">
              {/* Submitted */}
              {submission.created_at && (
                <div className="flex items-center text-sm gap-sm">
                  <span style={{ color: 'var(--color-success)' }}>Submitted by {submission.operator_name} on {formatDate(submission.created_at)}</span>
                </div>
              )}

              {/* Verified */}
              {submission.verified_at && (
                <div className="flex items-center text-sm gap-sm">
                  <span style={{ color: 'var(--color-success)' }}>Verified by {submission.verifier_name || 'Admin'} on {formatDate(submission.verified_at)}</span>
                </div>
              )}

              {/* Authorized */}
              {submission.authorized_at && (
                <div className="flex items-center text-sm gap-sm">
                  <span style={{ color: 'var(--color-success)' }}>Authorised by {submission.authorizer_name || 'Admin'} on {formatDate(submission.authorized_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {showReturnAction && (
            <button
              className="btn btn-secondary"
              onClick={() => onReturn?.(submission)}
              disabled={dwellActive}
              title={dwellTitle}
              style={dwellActive ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              Return to Operator
            </button>
          )}
          {showVerifyAction && (
            <button
              className="btn btn-primary"
              onClick={() => onVerify?.(submission)}
              disabled={dwellActive}
              title={dwellTitle}
              style={dwellActive ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              {dwellActive ? `Verify (${dwellRemaining}s)` : 'Verify'}
            </button>
          )}
          {showAuthorizeAction && (
            <button
              className={operatorMarkedNonCompliant ? 'btn btn-secondary' : 'btn btn-primary'}
              onClick={handleAuthoriseClick}
              disabled={dwellActive}
              title={dwellTitle}
              style={{
                ...(dwellActive ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                ...(operatorMarkedNonCompliant && !dwellActive
                  ? { background: 'var(--color-error)', borderColor: 'var(--color-error)', color: 'var(--color-white)' }
                  : {}),
              }}
            >
              {dwellActive
                ? `Authorise (${dwellRemaining}s)`
                : operatorMarkedNonCompliant
                  ? 'Authorise as Non-Compliant'
                  : 'Authorise'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CertSubmissionViewModal;
